import { useEffect, useRef, useState } from 'react';

import isEqual from 'lodash/isEqual';

import { useTheme } from '@mui/material/styles';

import {
  LENS_DATE_PATTERN_PRESETS,
  LENS_DATE_TOKENS,
  LENS_TIME_PATTERN_PRESETS,
  LENS_TIME_TOKENS,
  isValidDatePattern,
  isValidTimePattern,
} from '~/types/leLens';
import type {
  LensCard,
  LensCardConfig,
  LensCardType,
  LensDatePattern,
  LensLabelKind,
  LensMark,
  LensReferenceLine,
  LensSeriesSort,
  LensSortKey,
  LensTextScale,
  LensValueAxisConfig,
  LensValueFormat,
  LensValueFormatKind,
  LensValueLabelsConfig,
  LensXAxisConfig,
} from '~/types/leLens';

import { MAX_SERIES, formatLensValue, seriesColors } from '../../lensChart';

// Lifted verbatim from LensCardDialog.tsx's Format & style tab: every state
// variable, buildFormat, buildConfig, axisFieldProblem and the section
// summaries, unchanged. The only new behaviour is the onChange hand-off at
// the bottom, which fires the same buildConfig() output live preview needs
// on every change, instead of only once at Save.

export type StyleSection = 'commentary' | 'numbers' | 'axes' | 'series' | 'guides';

export type ReferenceLineDraft = {
  value: string;
  label: string;
  axis: NonNullable<LensReferenceLine['axis']>;
};

/** A stored bound as a form value: '' for absent, so the field reads as automatic. */
export function numberFieldValue(value: number | undefined): string {
  return value === undefined ? '' : String(value);
}

/** A form value as a stored bound, or undefined when the field is empty or not a number. */
export function numberFieldNumber(value: string): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }
  const numeric = Number(trimmed);
  return Number.isFinite(numeric) ? numeric : undefined;
}

/** Whether a field holds something that is neither empty nor a number. */
function isBadNumberField(value: string): boolean {
  return Boolean(value.trim()) && numberFieldNumber(value) === undefined;
}

/**
 * Builds the stored axis config, or undefined when every field is left alone.
 *
 * A title that is only whitespace is not a title: it would reserve room on the
 * plot for nothing.
 */
function buildValueAxis(title: string, min: string, max: string): LensValueAxisConfig | undefined {
  const axis: LensValueAxisConfig = {
    ...(title.trim() ? { title: title.trim() } : {}),
    ...(numberFieldNumber(min) === undefined ? {} : { min: numberFieldNumber(min) }),
    ...(numberFieldNumber(max) === undefined ? {} : { max: numberFieldNumber(max) }),
  };
  return Object.keys(axis).length ? axis : undefined;
}

export const TEXT_SCALES: Array<{ value: 'default' | LensTextScale; label: string }> = [
  { value: 'small', label: 'Small' },
  { value: 'default', label: 'Default' },
  { value: 'large', label: 'Large' },
];

export const formatKinds: Array<{ value: LensValueFormatKind; label: string }> = [
  { value: 'number', label: 'Number' },
  { value: 'currency', label: 'Currency' },
  { value: 'percent', label: 'Percent' },
];

export const currencies = ['AUD', 'NZD', 'USD', 'GBP', 'EUR', 'SGD', 'HKD'];

export const sortKeys: Array<{ value: LensSortKey; label: string }> = [
  { value: 'none', label: 'As the query returned it' },
  { value: 'label', label: 'By axis label' },
  { value: 'value', label: 'By value' },
];

// "First" is ambiguous on a stack: the first series is drawn at the base, which
// a reader reads as the bottom rather than the start.
export const seriesSorts: Array<{ value: LensSeriesSort; label: string }> = [
  { value: 'none', label: 'As the query returned it' },
  { value: 'largest', label: 'Largest at the bottom' },
  { value: 'smallest', label: 'Smallest at the bottom' },
  { value: 'alpha', label: 'A to Z, from the bottom' },
];

/** Shown beside each pattern so the choice is legible without guessing. */
export const PATTERN_SAMPLE = '2026-03-07';
export const TIME_PATTERN_SAMPLE = '13:45:30';

// The shared axis-chart builder is the only one that draws more than one
// series as a group: it is what stacking and series order mean, so those two
// options serve only the types it draws. Pie and scatter draw through their
// own builders and hold their own row order regardless of either setting; a
// metric, a table, a heatmap and a box plot have no series to group or
// reorder at all.
const SERIES_GROUPING_CARD_TYPES: ReadonlyArray<LensCardType> = ['line', 'bar', 'stacked', 'area', 'combo'];
// Pie and scatter still index this same palette by slice or by group, so
// colours reach two more types than stacking or series order do.
const COLOR_CARD_TYPES: ReadonlyArray<LensCardType> = [...SERIES_GROUPING_CARD_TYPES, 'pie', 'scatter'];
// config.sort only reaches the shared axis-chart model, through
// applyOrdering, and the table's own sortRows call. Pie, scatter, heatmap and
// box plot each build from their own hardcoded order, and a metric collapses
// to one row before order could mean anything.
const SORTABLE_CARD_TYPES: ReadonlyArray<LensCardType> = [...SERIES_GROUPING_CARD_TYPES, 'table'];
const COMMENTARY_CARD_TYPES: ReadonlyArray<LensCardType> = [
  'metric',
  ...SERIES_GROUPING_CARD_TYPES,
  'pie',
  'scatter',
  'heatmap',
  'boxplot',
];

/**
 * Whether a card type has a row order worth choosing. Exported so RowOrder.tsx
 * (the Data tab's own "Order rows" controls) and this hook's buildConfig
 * agree on exactly the same list, rather than keeping two copies that could
 * drift.
 */
export function cardShowsSort(type: LensCardType): boolean {
  return SORTABLE_CARD_TYPES.includes(type);
}

/**
 * Whether a card type groups more than one series together (and so can be
 * stacked). Exported so ChartTab's own Stacked switch and this hook's
 * buildConfig agree on exactly the same list.
 */
export function cardShowsSeriesGrouping(type: LensCardType): boolean {
  return SERIES_GROUPING_CARD_TYPES.includes(type);
}

/** Label choices that stay legible for each chart shape. The first choice is
 * the safe fallback when a card changes type and its old mode no longer fits. */
export function valueLabelModesForCardType(
  type: LensCardType,
): Array<{ label: string; value: NonNullable<LensValueLabelsConfig['mode']> }> {
  if (type === 'stacked') {
    return [
      { label: 'Totals only', value: 'totals' },
      { label: 'Every segment', value: 'values' },
    ];
  }
  if (type === 'bar') {
    return [
      { label: 'Every bar', value: 'values' },
      { label: 'Top 3 bars', value: 'top' },
    ];
  }
  return [
    { label: 'Latest value', value: 'last' },
    { label: 'Sampled values', value: 'values' },
  ];
}

export type UseStyleTabStateArgs = {
  card: LensCard;
  /** The card's current visual type (owned by the Chart tab); several
   *  controls here only apply to some types. */
  type: LensCardType;
  /** Declared columns, for the heatmap/box-plot grid-field gate. */
  columnNames: Array<string>;
  /** Series from the last render, for colours, marks and per-series labels. */
  seriesNames: Array<string>;
  /** The range the card actually plots, for the axis-floor refusal. */
  valueExtent?: { min: number; max: number };
  /**
   * Row order: "Order rows" / "Direction" / "Sort on" render on the Data
   * tab (they order the query's own rows, not this tab's presentation), so
   * this hook only reads their current value to fold into buildConfig's
   * `config.sort`. Owned and set by whichever caller renders the Data tab.
   */
  sortBy: LensSortKey;
  sortDirection: 'asc' | 'desc';
  sortSeries: string;
  /** Fired with the computed config on every change, the live-preview hand-off. */
  onChange?: (config: LensCardConfig) => void;
};

/**
 * All state and derivation behind the Format & style tab: every value
 * format, axis, series and reference-line control, plus the section
 * summaries and the refusal a bad axis bound produces. Kept apart from
 * rendering the same way `usePanelState` keeps the Data tab's rules apart
 * from its own JSX.
 */
export function useStyleTabState({
  card,
  columnNames,
  onChange,
  seriesNames,
  sortBy,
  sortDirection,
  sortSeries,
  type,
  valueExtent,
}: UseStyleTabStateArgs) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isChartDescription = COMMENTARY_CARD_TYPES.includes(type) && type !== 'metric';

  const [kind, setKind] = useState<LensValueFormatKind>('number');
  const [currency, setCurrency] = useState('AUD');
  const [decimals, setDecimals] = useState<string>('auto');
  const [percentScale, setPercentScale] = useState<'percent' | 'unit'>('percent');
  const [suffix, setSuffix] = useState('');
  const valueFormatBaseline = useRef<{ key: string; format?: LensValueFormat }>({ key: '' });
  const [stacked, setStacked] = useState(false);
  const [barOrientation, setBarOrientation] = useState<'vertical' | 'horizontal'>('vertical');
  const [lineStyle, setLineStyle] = useState<'smooth' | 'straight'>('straight');
  const [colors, setColors] = useState<Array<string>>([]);
  const [seriesSort, setSeriesSort] = useState<LensSeriesSort>('none');
  const [fieldFormats, setFieldFormats] = useState<Record<string, LensValueFormat>>({});
  const [fieldLabels, setFieldLabels] = useState<Record<string, string>>({});
  const [editingField, setEditingField] = useState<string | null>(null);
  const [fieldLabelDraft, setFieldLabelDraft] = useState('');
  const [labelKind, setLabelKind] = useState<LensLabelKind>('text');
  const [labelPattern, setLabelPattern] = useState<LensDatePattern>('dd/MM/yyyy');
  const [isCustomPattern, setIsCustomPattern] = useState(false);
  const [xColumn, setXColumn] = useState('');
  const [yColumn, setYColumn] = useState('');
  const [categoryColumn, setCategoryColumn] = useState('');
  const [valueColumn, setValueColumn] = useState('');
  const [xAxisDensity, setXAxisDensity] = useState<NonNullable<LensXAxisConfig['tickDensity']>>('auto');
  const [xAxisRotation, setXAxisRotation] = useState<NonNullable<LensXAxisConfig['tickRotation']>>(0);
  // Axis bounds and titles are held as strings, with '' meaning "leave it to the
  // card". A number state would have to pick a sentinel for empty, and 0 is a
  // floor someone may genuinely want.
  const [xAxisTitle, setXAxisTitle] = useState('');
  const [yAxisTitle, setYAxisTitle] = useState('');
  const [yAxisMin, setYAxisMin] = useState('');
  const [yAxisMax, setYAxisMax] = useState('');
  const [y2AxisTitle, setY2AxisTitle] = useState('');
  const [y2AxisMin, setY2AxisMin] = useState('');
  const [y2AxisMax, setY2AxisMax] = useState('');
  // Which series sit on the second scale, and what each series is drawn as.
  const [secondarySeries, setSecondarySeries] = useState<Array<string>>([]);
  const [seriesMarks, setSeriesMarks] = useState<Record<string, LensMark>>({});
  const [valueLabelsOn, setValueLabelsOn] = useState(false);
  const [valueLabelMode, setValueLabelMode] = useState<'values' | 'totals' | 'top' | 'last'>('values');
  const [valueLabelSize, setValueLabelSize] = useState<LensTextScale>('medium');
  const [valueLabelSeries, setValueLabelSeries] = useState<Array<string>>([]);
  const [textScale, setTextScale] = useState<'default' | LensTextScale>('default');
  const [referenceLines, setReferenceLines] = useState<Array<ReferenceLineDraft>>([]);
  const [commentaryVisible, setCommentaryVisible] = useState(true);
  const [commentaryPosition, setCommentaryPosition] = useState<'right' | 'below'>(
    isChartDescription ? 'below' : 'right',
  );
  const [styleSection, setStyleSection] = useState<StyleSection | null>('commentary');
  const toggleStyleSection = (section: StyleSection) =>
    setStyleSection((current) => (current === section ? null : section));

  // Seed from the card only when a different card is opened, the same guard
  // LensCardDialog's own seeding effect used: keying on the card object
  // would re-seed on every lens refetch and discard an edit in progress.
  const cardId = card.id;
  const [savedConfigRevision, setSavedConfigRevision] = useState(0);
  const seedKey = `${cardId}:${savedConfigRevision}`;
  const ownConfigs = useRef<Array<LensCardConfig>>([]);
  const [seededCardId, setSeededCardId] = useState<string | null>(null);
  const previewBaseline = useRef<{ seedKey: string; initial: string; last: string } | null>(null);
  useEffect(() => {
    ownConfigs.current = [JSON.parse(JSON.stringify(card.config ?? {}))];
    const config = card.config ?? {};
    valueFormatBaseline.current = {
      format: config.format,
      key: JSON.stringify([
        config.format?.kind ?? 'number',
        config.format?.currency ?? 'AUD',
        config.format?.decimals === undefined ? 'auto' : String(config.format.decimals),
        config.format?.percentScale ?? 'percent',
        config.format?.suffix ?? '',
      ]),
    };
    setKind(config.format?.kind ?? 'number');
    setCurrency(config.format?.currency ?? 'AUD');
    setDecimals(config.format?.decimals === undefined ? 'auto' : String(config.format.decimals));
    setPercentScale(config.format?.percentScale ?? 'percent');
    setSuffix(config.format?.suffix ?? '');
    setStacked(config.stacked ?? card.type === 'stacked');
    setBarOrientation(config.barOrientation ?? 'vertical');
    setLineStyle(config.lineStyle ?? 'straight');
    setColors(config.colors ?? []);
    setSeriesSort(config.seriesSort ?? 'none');
    setFieldFormats(config.fieldFormats ?? {});
    setFieldLabels(config.fieldLabels ?? {});
    setEditingField(null);
    setFieldLabelDraft('');
    setLabelKind(config.labelFormat?.kind ?? 'text');
    setLabelPattern(config.labelFormat?.pattern ?? 'dd/MM/yyyy');
    const labelPatterns = config.labelFormat?.kind === 'time' ? LENS_TIME_PATTERN_PRESETS : LENS_DATE_PATTERN_PRESETS;
    setIsCustomPattern(
      Boolean(config.labelFormat?.pattern) && !labelPatterns.includes(config.labelFormat.pattern as never),
    );
    setXColumn(config.xColumn ?? '');
    setYColumn(config.yColumn ?? '');
    setCategoryColumn(config.categoryColumn ?? '');
    setValueColumn(config.valueColumn ?? '');
    setXAxisDensity(config.xAxis?.tickDensity ?? 'auto');
    setXAxisRotation(config.xAxis?.tickRotation ?? 0);
    setXAxisTitle(config.xAxis?.title ?? '');
    setYAxisTitle(config.yAxis?.title ?? '');
    setYAxisMin(numberFieldValue(config.yAxis?.min));
    setYAxisMax(numberFieldValue(config.yAxis?.max));
    setY2AxisTitle(config.secondaryYAxis?.title ?? '');
    setY2AxisMin(numberFieldValue(config.secondaryYAxis?.min));
    setY2AxisMax(numberFieldValue(config.secondaryYAxis?.max));
    setSecondarySeries(config.secondaryAxis ?? []);
    setSeriesMarks(config.seriesMarks ?? {});
    setValueLabelsOn(config.valueLabels?.show ?? false);
    setValueLabelMode(config.valueLabels?.mode ?? (type === 'stacked' ? 'totals' : type === 'bar' ? 'values' : 'last'));
    setValueLabelSize(config.valueLabels?.size ?? 'medium');
    setValueLabelSeries(config.valueLabels?.series ?? []);
    setTextScale(config.textScale ?? 'default');
    setReferenceLines(
      (config.referenceLines ?? []).map((line) => ({
        value: String(line.value),
        label: line.label ?? '',
        axis: line.axis ?? 'primary',
      })),
    );
    setCommentaryVisible(config.commentary?.show ?? true);
    // Legacy right-hand prose is now a collapsed description beneath the plot.
    // Keep the contract field so older saved cards remain compatible.
    setCommentaryPosition(isChartDescription ? 'below' : (config.commentary?.position ?? 'right'));
    // Every series-grouping type also carries commentary (COMMENTARY_CARD_TYPES
    // spreads SERIES_GROUPING_CARD_TYPES in), so this only ever chooses between
    // Commentary and Numbers.
    setStyleSection(COMMENTARY_CARD_TYPES.includes(type) ? 'commentary' : 'numbers');
    setSeededCardId(seedKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedKey]);

  const incomingConfigKey = JSON.stringify(card.config ?? {});
  useEffect(() => {
    const incoming = JSON.parse(incomingConfigKey);
    if (!ownConfigs.current.some((own) => isEqual(own, incoming))) {
      setSavedConfigRevision((revision) => revision + 1);
    }
  }, [incomingConfigKey]);

  const showsSeriesGrouping = SERIES_GROUPING_CARD_TYPES.includes(type);
  const showsColors = COLOR_CARD_TYPES.includes(type);
  const showsSort = cardShowsSort(type);
  const showsCommentary = COMMENTARY_CARD_TYPES.includes(type);
  // A text insight is generated text, not a query result: it never touches
  // card.config, so no display option below reaches anything for it.
  const showsValueFormat = type !== 'insight';
  // A box plot groups by categoryColumn rather than xColumn, so the x-axis
  // field picker would write a setting the renderer never reads for it.
  const showsXAxis = !['metric', 'table', 'pie', 'scatter', 'insight', 'boxplot'].includes(type);
  const showsValueAxis = SERIES_GROUPING_CARD_TYPES.includes(type);
  // Whether the Axes section has anything in it for this card: a value axis, an
  // x axis, or the grid fields a heatmap and a box plot read directly.
  const showsGridFields = (type === 'heatmap' || type === 'boxplot') && columnNames.length > 0;
  const showsAnyAxis = showsValueAxis || showsXAxis || showsGridFields;
  // A stack's floor is always zero: cutting the bottom segment misstates every
  // proportion above it, so offering the field would be offering a setting
  // with no effect.
  const showsAxisFloor = showsValueAxis && !(type === 'stacked' || stacked);
  // Only the shared axis-chart renderer reads config.labelFormat.
  const showsLabelFormat = showsValueAxis;
  const isFormattedLabel = labelKind === 'date' || labelKind === 'time';
  const labelPatterns = labelKind === 'time' ? LENS_TIME_PATTERN_PRESETS : LENS_DATE_PATTERN_PRESETS;
  const labelPatternIsValid =
    labelKind === 'time' ? isValidTimePattern(labelPattern.trim()) : isValidDatePattern(labelPattern.trim());
  const labelPatternSample = labelKind === 'time' ? TIME_PATTERN_SAMPLE : PATTERN_SAMPLE;
  const labelPatternTokens = labelKind === 'time' ? LENS_TIME_TOKENS : LENS_DATE_TOKENS;
  const swatchCount = Math.min(Math.max(seriesNames.length, 1), MAX_SERIES);
  const defaultColors = seriesColors(isDark);

  /**
   * The value format the form currently describes. buildConfig writes this
   * onto the card, and the axis messages read it so a refused bound names
   * its number the way the card will draw it: "$3,900", not "3900".
   */
  const buildFormat = (): LensValueFormat => {
    const trimmedSuffix = suffix.trim();
    return {
      kind,
      ...(kind === 'currency' ? { currency } : {}),
      ...(kind === 'percent' ? { percentScale } : {}),
      ...(decimals === 'auto' ? {} : { decimals: Number(decimals) }),
      ...(trimmedSuffix ? { suffix: trimmedSuffix } : {}),
    };
  };

  const buildConfig = (): LensCardConfig => {
    const config: LensCardConfig = { ...(card.config ?? {}) };
    const labelModes = valueLabelModesForCardType(type);
    const effectiveValueLabelMode = labelModes.some((option) => option.value === valueLabelMode)
      ? valueLabelMode
      : labelModes[0].value;

    // Same reasoning for every branch below: a hidden control's state still
    // tracks whatever the card already had (the seeding effect above reads it
    // regardless of type), so a card saved while its own section is hidden
    // must leave that stored value alone rather than overwrite it with the
    // form's defaults.
    // An omitted format lets dataset metadata supply the currency/ratio.
    // Saving a title or colour must not replace that with the form's Number default.
    if (showsValueFormat) {
      if (JSON.stringify([kind, currency, decimals, percentScale, suffix]) !== valueFormatBaseline.current.key) {
        config.format = buildFormat();
      } else if (valueFormatBaseline.current.format) {
        config.format = valueFormatBaseline.current.format;
      } else {
        delete config.format;
      }
    }

    // Save chart descriptions explicitly below so reloads agree with the renderer,
    // including cards whose legacy contract still carries a right-hand position.
    if (showsCommentary) {
      config.commentary = {
        ...(!commentaryVisible ? { show: false } : {}),
        ...(type === 'metric' ? {} : { position: 'below' }),
      };
    } else {
      delete config.commentary;
    }

    const chosen = colors.filter(Boolean);
    if (chosen.length) {
      config.colors = chosen;
    } else {
      delete config.colors;
    }

    if (type === 'bar' || type === 'stacked') {
      config.barOrientation = barOrientation;
    }

    if (showsSeriesGrouping) {
      config.stacked = stacked;
    }

    if (type === 'line' && lineStyle === 'smooth') {
      config.lineStyle = 'smooth';
    } else {
      delete config.lineStyle;
    }

    if (showsSort) {
      if (sortBy === 'none') {
        delete config.sort;
      } else {
        config.sort = {
          by: sortBy,
          direction: sortDirection,
          ...(sortBy === 'value' && sortSeries ? { series: sortSeries } : {}),
        };
      }
    }

    if (showsSeriesGrouping) {
      if (seriesSort === 'none') {
        delete config.seriesSort;
      } else {
        config.seriesSort = seriesSort;
      }
    }

    if (showsValueFormat) {
      if (Object.keys(fieldFormats).length) {
        config.fieldFormats = fieldFormats;
      } else {
        delete config.fieldFormats;
      }
      const namedFields = Object.fromEntries(
        Object.entries(fieldLabels)
          .map(([field, label]) => [field, label.trim()] as const)
          .filter(([, label]) => Boolean(label)),
      );
      if (Object.keys(namedFields).length) {
        config.fieldLabels = namedFields;
      } else {
        delete config.fieldLabels;
      }
    }

    if ((labelKind === 'date' || labelKind === 'time') && labelPatternIsValid) {
      config.labelFormat = { kind: labelKind, pattern: labelPattern.trim() };
    } else {
      delete config.labelFormat;
    }

    if (xColumn) {
      config.xColumn = xColumn;
    } else {
      delete config.xColumn;
    }

    if (yColumn) {
      config.yColumn = yColumn;
    } else {
      delete config.yColumn;
    }

    if (categoryColumn) {
      config.categoryColumn = categoryColumn;
    } else {
      delete config.categoryColumn;
    }

    if (valueColumn) {
      config.valueColumn = valueColumn;
    } else {
      delete config.valueColumn;
    }

    if (xAxisDensity === 'all' || xAxisRotation !== 0 || xAxisTitle.trim()) {
      config.xAxis = {
        ...(xAxisDensity === 'all' ? { tickDensity: 'all' as const } : {}),
        ...(xAxisRotation ? { tickRotation: xAxisRotation } : {}),
        ...(xAxisTitle.trim() ? { title: xAxisTitle.trim() } : {}),
      };
    } else {
      delete config.xAxis;
    }

    if (showsValueAxis) {
      const yAxis = buildValueAxis(yAxisTitle, showsAxisFloor ? yAxisMin : '', yAxisMax);
      if (yAxis) {
        config.yAxis = yAxis;
      } else {
        delete config.yAxis;
      }

      // A second scale only exists on a combo, so a card that stops being one
      // should not keep a bound, a split or a mark that nothing reads.
      if (type === 'combo') {
        const chosenSecondary = secondarySeries.filter((name) => seriesNames.includes(name));
        // An empty list is not "no series on the second scale", it is "work it
        // out from the data", which is what the card did before anyone asked.
        if (chosenSecondary.length) {
          config.secondaryAxis = chosenSecondary;
        } else {
          delete config.secondaryAxis;
        }

        const marks = Object.fromEntries(
          Object.entries(seriesMarks).filter(([name, mark]) => seriesNames.includes(name) && mark),
        );
        if (Object.keys(marks).length) {
          config.seriesMarks = marks;
        } else {
          delete config.seriesMarks;
        }
      } else {
        delete config.secondaryAxis;
        delete config.seriesMarks;
      }

      const y2Axis = type === 'combo' ? buildValueAxis(y2AxisTitle, y2AxisMin, y2AxisMax) : undefined;
      if (y2Axis) {
        config.secondaryYAxis = y2Axis;
      } else {
        delete config.secondaryYAxis;
      }

      if (valueLabelsOn) {
        // An empty list and a full list both mean every series, and the stored
        // shape says so by omitting the list.
        const chosenLabels = valueLabelSeries.filter((name) => seriesNames.includes(name));
        config.valueLabels = {
          show: true,
          ...(effectiveValueLabelMode === 'values' ? {} : { mode: effectiveValueLabelMode }),
          ...(valueLabelSize === 'medium' ? {} : { size: valueLabelSize }),
          ...(chosenLabels.length && chosenLabels.length < seriesNames.length ? { series: chosenLabels } : {}),
        };
      } else {
        delete config.valueLabels;
      }

      if (textScale === 'default') {
        delete config.textScale;
      } else {
        config.textScale = textScale;
      }

      const guides = referenceLines
        .map((line) => ({
          value: Number(line.value),
          ...(line.label.trim() ? { label: line.label.trim() } : {}),
          ...(type === 'combo' && line.axis === 'secondary' ? { axis: 'secondary' as const } : {}),
        }))
        .filter((line) => Number.isFinite(line.value));
      if (guides.length) {
        config.referenceLines = guides;
      } else {
        delete config.referenceLines;
      }
    }

    return config;
  };

  // A floor above zero, as typed. Read from the field rather than the saved
  // config so the warning appears while someone is deciding, not after saving.
  const truncatesFromZero = showsAxisFloor && (numberFieldNumber(yAxisMin) ?? 0) > 0;

  /**
   * What each collapsed section already has set. These sections replace one
   * long scroll of controls; collapsing them only helps if a closed header
   * still answers "is my thing in there", so each one lists its own
   * non-default values and falls back to naming what it holds.
   */
  const summaryOf = (parts: Array<string | false | undefined>, fallback: string): string => {
    const set = parts.filter(Boolean) as Array<string>;
    return set.length ? set.join(' · ') : fallback;
  };
  const numbersSummary = summaryOf(
    [
      showsValueFormat && `${kind === 'currency' ? currency : kind === 'percent' ? 'Percent' : 'Number'}`,
      decimals !== 'auto' && `${decimals} decimals`,
      suffix.trim() && `suffix “${suffix.trim()}”`,
      Object.keys(fieldFormats).length > 0 && `${Object.keys(fieldFormats).length} field overrides`,
      Object.keys(fieldLabels).length > 0 && `${Object.keys(fieldLabels).length} renamed`,
    ],
    'Value format, decimals and per-field overrides.',
  );
  const axesSummary = summaryOf(
    [
      yAxisTitle.trim() && `Y titled “${yAxisTitle.trim()}”`,
      yAxisMin.trim() && `from ${yAxisMin.trim()}`,
      yAxisMax.trim() && `to ${yAxisMax.trim()}`,
      xAxisTitle.trim() && `X titled “${xAxisTitle.trim()}”`,
      type === 'combo' && secondarySeries.length > 0 && `${secondarySeries.length} on the second scale`,
      xAxisRotation !== 0 && `labels at ${xAxisRotation}°`,
      xAxisDensity === 'all' && 'every category',
    ],
    'Axis titles, bounds, labels and the second scale.',
  );
  // Stacking itself is surfaced on Visual now (ChartTab's own switch), so it
  // is left out here to avoid naming the same setting in two places.
  const seriesSummary = summaryOf(
    [
      seriesSort !== 'none' && 'reordered',
      lineStyle === 'smooth' && 'smoothed',
      colors.filter(Boolean).length > 0 && `${colors.filter(Boolean).length} colours set`,
    ],
    'Order, line style and colours.',
  );
  const guidesSummary = referenceLines.length
    ? `${referenceLines.length} ${referenceLines.length === 1 ? 'guide' : 'guides'} set`
    : 'Targets, benchmarks and thresholds.';
  const commentarySummary = commentaryVisible
    ? isChartDescription
      ? 'Collapsed below chart'
      : 'Shown below'
    : 'Hidden';

  const styleSections: Array<{ id: StyleSection; label: string; summary: string }> = [
    ...(showsCommentary
      ? [
          {
            id: 'commentary' as const,
            label: isChartDescription ? 'Chart description' : 'Commentary',
            summary: commentarySummary,
          },
        ]
      : []),
    ...(showsValueFormat ? [{ id: 'numbers' as const, label: 'Numbers', summary: numbersSummary }] : []),
    ...(showsAnyAxis ? [{ id: 'axes' as const, label: 'Axes', summary: axesSummary }] : []),
    // Colours no longer have a control here (chart colours come from
    // lensChart.ts by decision), so a type with no series grouping has
    // nothing left for this section to show.
    ...(showsSeriesGrouping ? [{ id: 'series' as const, label: 'Series', summary: seriesSummary }] : []),
    ...(showsValueAxis ? [{ id: 'guides' as const, label: 'Reference lines', summary: guidesSummary }] : []),
  ];
  const activeStyleSection =
    styleSection === null
      ? null
      : styleSections.some((entry) => entry.id === styleSection)
        ? styleSection
        : styleSections[0]?.id;

  /**
   * The first thing wrong with the axis fields, or null. The floor check is
   * the one that matters: both renderers clip a mark to the plot area, so a
   * floor above any plotted value does not shorten that mark, it removes it,
   * and a missing bar reads as zero.
   */
  const axisFieldProblem = (): string | null => {
    const fields: Array<[string, string]> = [
      ...(showsAxisFloor ? ([['Y-axis minimum', yAxisMin]] as Array<[string, string]>) : []),
      ['Y-axis maximum', yAxisMax],
      ...(type === 'combo'
        ? ([
            ['Second axis minimum', y2AxisMin],
            ['Second axis maximum', y2AxisMax],
          ] as Array<[string, string]>)
        : []),
    ];
    for (const [label, value] of fields) {
      if (isBadNumberField(value)) {
        return `${label} must be a number, or empty to let the card choose.`;
      }
    }

    const min = showsAxisFloor ? numberFieldNumber(yAxisMin) : undefined;
    const max = numberFieldNumber(yAxisMax);
    if (min !== undefined && max !== undefined && min >= max) {
      return 'The y-axis minimum has to be below the maximum.';
    }
    if (min !== undefined && valueExtent && min > valueExtent.min) {
      return `The lowest value on this card is ${formatLensValue(valueExtent.min, buildFormat())}. An axis starting above it would leave that mark off the chart entirely, so the minimum has to be at or below it.`;
    }
    if (max !== undefined && valueExtent && max < valueExtent.max) {
      return `The highest value on this card is ${formatLensValue(valueExtent.max, buildFormat())}. An axis ending below it would cut that mark off, so the maximum has to be at or above it.`;
    }

    if (type === 'combo') {
      const secondMin = numberFieldNumber(y2AxisMin);
      const secondMax = numberFieldNumber(y2AxisMax);
      if (secondMin !== undefined && secondMax !== undefined && secondMin >= secondMax) {
        return 'The second axis minimum has to be below its maximum.';
      }
    }

    const invalidGuide = referenceLines.find((line) => !line.value.trim() || !Number.isFinite(Number(line.value)));
    if (invalidGuide) {
      return 'Every reference line needs a numeric value.';
    }

    return null;
  };

  // Action helpers for the mutations the legacy tab tied to an explicit
  // markDirty() call (a button click with no underlying <input> change event
  // to carry it): renaming a field, and the reference-line and colour resets.
  function saveFieldLabel(field: string): void {
    setFieldLabels((current) => ({ ...current, [field]: fieldLabelDraft }));
    setEditingField(null);
  }

  function startEditingField(field: string): void {
    setFieldLabelDraft(fieldLabels[field] ?? '');
    setEditingField(field);
  }

  function addReferenceLine(): void {
    setReferenceLines((current) => [...current, { value: '', label: '', axis: 'primary' }]);
  }

  function removeReferenceLine(index: number): void {
    setReferenceLines((current) => current.filter((_entry, entryIndex) => entryIndex !== index));
  }

  function updateReferenceLine(index: number, patch: Partial<ReferenceLineDraft>): void {
    setReferenceLines((current) =>
      current.map((entry, entryIndex) => (entryIndex === index ? { ...entry, ...patch } : entry)),
    );
  }

  function resetColors(): void {
    setColors([]);
  }

  function setColorAt(index: number, value: string): void {
    const next = [...colors];
    while (next.length <= index) {
      next.push(defaultColors[next.length]);
    }
    next[index] = value;
    setColors(next);
  }

  function setFieldOverride(field: string, next: LensValueFormat | null): void {
    setFieldFormats((current) => {
      const updated = { ...current };
      if (next) {
        updated[field] = next;
      } else {
        delete updated[field];
      }
      return updated;
    });
  }

  // Building a config normalizes omitted defaults. That is not an edit. Wait
  // for this card's seed to render, then publish only changes to editable
  // values. Result-column refreshes and previous-card state must never dirty
  // a chart simply because its editor was opened.
  const editableKey = JSON.stringify([
    kind,
    currency,
    decimals,
    percentScale,
    suffix,
    stacked,
    barOrientation,
    lineStyle,
    colors,
    seriesSort,
    fieldFormats,
    fieldLabels,
    labelKind,
    labelPattern,
    xColumn,
    yColumn,
    categoryColumn,
    valueColumn,
    xAxisDensity,
    xAxisRotation,
    xAxisTitle,
    yAxisTitle,
    yAxisMin,
    yAxisMax,
    y2AxisTitle,
    y2AxisMin,
    y2AxisMax,
    secondarySeries,
    seriesMarks,
    valueLabelsOn,
    valueLabelMode,
    valueLabelSize,
    valueLabelSeries,
    textScale,
    referenceLines,
    commentaryVisible,
    commentaryPosition,
    type,
    sortBy,
    sortDirection,
    sortSeries,
  ]);
  useEffect(() => {
    const incoming = JSON.parse(incomingConfigKey);
    if (seededCardId !== seedKey || !ownConfigs.current.some((own) => isEqual(own, incoming))) {
      return;
    }
    const baseline = previewBaseline.current;
    if (baseline?.seedKey !== seedKey) {
      previewBaseline.current = { seedKey, initial: editableKey, last: editableKey };
      return;
    }
    if (baseline.last === editableKey) {
      return;
    }
    baseline.last = editableKey;
    // Reverting every control also restores the exact saved representation,
    // including omitted defaults, so the saved/unsaved indicator is truthful.
    const published = editableKey === baseline.initial ? { ...(card.config ?? {}) } : buildConfig();
    ownConfigs.current = [JSON.parse(JSON.stringify(published))];
    onChange?.(published);
    // The editable values are the hand-off key; incoming rows and callback
    // identity do not constitute user edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedKey, seededCardId, editableKey]);

  return {
    activeStyleSection,
    axisFieldProblem,
    buildConfig,
    buildFormat,
    categoryColumn,
    commentaryPosition,
    commentaryVisible,
    colors,
    currency,
    decimals,
    defaultColors,
    editingField,
    setEditingField,
    fieldFormats,
    fieldLabelDraft,
    fieldLabels,
    isCustomPattern,
    isFormattedLabel,
    kind,
    labelKind,
    labelPattern,
    labelPatterns,
    labelPatternIsValid,
    labelPatternSample,
    labelPatternTokens,
    lineStyle,
    percentScale,
    referenceLines,
    secondarySeries,
    seriesMarks,
    seriesSort,
    setCategoryColumn,
    setCommentaryPosition,
    setCommentaryVisible,
    setColorAt,
    setColors,
    setCurrency,
    setDecimals,
    setFieldLabelDraft,
    setFieldOverride,
    setIsCustomPattern,
    setKind,
    setLabelKind,
    setLabelPattern,
    setLineStyle,
    setPercentScale,
    setSecondarySeries,
    setSeriesMarks,
    setSeriesSort,
    setStacked,
    setBarOrientation,
    setStyleSection,
    toggleStyleSection,
    setSuffix,
    setTextScale,
    setValueColumn,
    setValueLabelMode,
    setValueLabelSeries,
    setValueLabelSize,
    setValueLabelsOn,
    setXAxisDensity,
    setXAxisRotation,
    setXAxisTitle,
    setXColumn,
    setY2AxisMax,
    setY2AxisMin,
    setY2AxisTitle,
    setYAxisMax,
    setYAxisMin,
    setYAxisTitle,
    setYColumn,
    showsAnyAxis,
    showsAxisFloor,
    showsColors,
    showsCommentary,
    showsGridFields,
    showsLabelFormat,
    showsSeriesGrouping,
    showsSort,
    showsValueAxis,
    showsValueFormat,
    showsXAxis,
    stacked,
    barOrientation,
    startEditingField,
    saveFieldLabel,
    addReferenceLine,
    removeReferenceLine,
    updateReferenceLine,
    resetColors,
    styleSection,
    styleSections,
    suffix,
    swatchCount,
    textScale,
    truncatesFromZero,
    valueColumn,
    valueLabelMode,
    valueLabelSeries,
    valueLabelSize,
    valueLabelsOn,
    xAxisDensity,
    xAxisRotation,
    xAxisTitle,
    xColumn,
    y2AxisMax,
    y2AxisMin,
    y2AxisTitle,
    yAxisMax,
    yAxisMin,
    yAxisTitle,
    yColumn,
  };
}

export type StyleTabState = ReturnType<typeof useStyleTabState>;
